import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Modal } from '../components/ui/Modal'
import { TrainingForm } from '../components/forms/TrainingForm'
import { trainingService } from '../services/trainingService'
import { userService } from '../services/userService'
import { getCalendarBadgeData, isTrainingPast, cn } from '../utils'
import { PlusIcon, UsersIcon, CalendarIcon, CheckCircleIcon } from 'lucide-react'

export const DashboardPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Fetch trainings data
  const { data: trainingsData, isLoading: trainingsLoading } = useQuery({
    queryKey: ['trainings', { status: 'future', limit: 10 }],
    queryFn: () => trainingService.getTrainings({ status: 'future', limit: 10 }),
  })

  // Fetch users stats
  const { data: usersStats, isLoading: usersLoading } = useQuery({
    queryKey: ['users-stats'],
    queryFn: () => userService.getUserStats(),
  })

  const trainings = trainingsData?.data?.data || []
  const total = trainingsData?.data?.total || 0
  const stats = [
    {
      label: 'Aktiva träningar',
      value: total.toString(),
      color: 'primary' as const,
      icon: CalendarIcon,
    },
    {
      label: 'Totalt användare',
      value: usersStats?.data?.total_users?.toString() || '0',
      color: 'success' as const,
      icon: UsersIcon,
    },
    {
      label: 'Genomförda idag',
      value: trainings.filter(t => isTrainingPast(t.datum) && new Date(t.datum).toDateString() === new Date().toDateString()).length.toString(),
      color: 'warning' as const,
      icon: CheckCircleIcon,
    },
    {
      label: 'Kommande träningar',
      value: trainings.filter(t => !isTrainingPast(t.datum)).length.toString(),
      color: 'info' as const,
      icon: CalendarIcon,
    },
  ]



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Träningar</h1>
          <p className="text-gray-600">Hantera dina träningar och användare</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Lägg till träning
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {usersLoading || trainingsLoading ? (
                      <div className="animate-pulse h-8 bg-gray-200 rounded w-16"></div>
                    ) : (
                      stat.value
                    )}
                  </p>
                </div>
                <div className={cn(
                  "h-12 w-12 rounded-full flex items-center justify-center",
                  `bg-${stat.color}-100`
                )}>
                  <stat.icon className={cn("h-6 w-6", `text-${stat.color}-600`)} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upcoming Trainings */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Kommande träningar</h3>
            <Link to="/archive">
              <Button variant="outline" size="sm">
                Visa arkiv
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {trainingsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-200 rounded"></div>
                    <div className="space-y-2">
                      <div className="w-32 h-4 bg-gray-200 rounded"></div>
                      <div className="w-24 h-3 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="w-16 h-8 bg-gray-200 rounded"></div>
                    <div className="w-16 h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : trainings.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Inga kommande träningar</p>
              <Button>
                <PlusIcon className="h-4 w-4 mr-2" />
                Skapa din första träning
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {trainings.slice(0, 5).map((training) => {
                const calendarData = getCalendarBadgeData(training.datum)
                return (
                  <div
                    key={training.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {/* Calendar Badge */}
                    <div className="text-center min-w-0">
                      <div className="text-sm font-semibold text-gray-900">{calendarData.day}</div>
                      <div className="text-xs text-gray-500">{calendarData.month}</div>
                      <div className="text-xs text-gray-500">{calendarData.time}</div>
                    </div>

                    {/* Training Info */}
                    <div className="flex-1 ml-4 min-w-0">
                      <Link to={`/training/${training.id}`}>
                        <h4 className="text-sm font-medium text-gray-900 hover:text-primary-600 truncate">
                          {training.titel}
                        </h4>
                      </Link>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        {training.location && (
                          <>
                            <span>{training.location}</span>
                            <span>•</span>
                          </>
                        )}
                        <span>{training.anmalda || 0} anmälda</span>
                        {training.max_platser && (
                          <>
                            <span>/</span>
                            <span>{training.max_platser}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Badges and Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {/* Badges */}
                      <div className="flex space-x-1">
                        {training.bootcamp && (
                          <Badge variant="info">
                            {training.bootcamp_name ? `Bootcamp: ${training.bootcamp_name}` : 'Bootcamp'}
                          </Badge>
                        )}
                        {training.free_training && <Badge variant="success">Gratis</Badge>}
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <Link to={`/training/${training.id}`}>
                          <Button variant="outline" size="sm">
                            Redigera
                          </Button>
                        </Link>
                        <Link to={`/training/${training.id}/attendance`}>
                          <Button variant="outline" size="sm">
                            Närvaro
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                )
              })}
              {trainings.length > 5 && (
                <div className="text-center pt-4">
                  <Link to="/archive">
                    <Button variant="ghost">
                      Visa alla {trainings.length} träningar
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Training Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Skapa ny träning"
        size="lg"
      >
        <TrainingForm
          onSuccess={() => setIsCreateModalOpen(false)}
          onCancel={() => setIsCreateModalOpen(false)}
        />
      </Modal>
    </div>
  )
}
